import { useMemo, useCallback } from "react";
import { SectionCard } from "./SectionCard";
import { TransitionActionRow } from "./TransitionActionRow";
import { TransitionOtherActionCard } from "./TransitionOtherActionCard";
import { TransitionReusePicker } from "./TransitionReusePicker";
import { TRANSITION_CATALOG } from "@shared/transitionCatalog";
import { useWizard } from "@/contexts/WizardContext";
import type { TransitionMeasure } from "@/contexts/wizard/types";

export interface TransitionMeasuresBlockProps {
  topicId: string;
  currentTab: "environment" | "social" | "governance";
  disabled?: boolean;
}

function newMeasure(topicId: string, actionId: string, overrides: Partial<TransitionMeasure> = {}): TransitionMeasure {
  return {
    id: crypto.randomUUID(),
    topicId,
    actionId,
    checked: true,
    metricType: "none",
    ...overrides,
  };
}

export function TransitionMeasuresBlock({ topicId, currentTab, disabled }: TransitionMeasuresBlockProps) {
  const { transitionMeasuresByTopic, updateTransitionMeasuresForTopic, policyTopics } = useWizard();
  const catalog = TRANSITION_CATALOG[topicId];
  const measures = transitionMeasuresByTopic[topicId] || [];
  const allMeasures = useMemo(
    () => Object.values(transitionMeasuresByTopic).flat(),
    [transitionMeasuresByTopic]
  );

  const setTopicMeasures = useCallback(
    (updater: (prev: TransitionMeasure[]) => TransitionMeasure[]) => {
      updateTransitionMeasuresForTopic(topicId, updater(measures));
    },
    [measures, topicId, updateTransitionMeasuresForTopic]
  );

  if (!catalog) return null;

  const otherMeasures = measures.filter((m) => m.actionId === "other");

  return (
    <SectionCard header={<h3 className="text-[1.05rem] font-bold leading-7 text-slate-900">{catalog.sectionTitle}</h3>}>
      <div className="space-y-4">
        {catalog.actions.map((action) => {
          const measure = measures.find((m) => m.actionId === action.id);
          return (
            <TransitionActionRow
              key={action.id}
              action={action}
              measure={measure}
              metricUnits={catalog.metricUnits}
              policyOptions={policyTopics}
              disabled={disabled}
              currentTab={currentTab}
              fieldIdentifierPrefix={`transition-${topicId}`}
              onToggle={(checked) =>
                setTopicMeasures((prev) => {
                  if (!checked) return prev.filter((m) => m.actionId !== action.id);
                  if (measure) return prev;
                  return [...prev, newMeasure(topicId, action.id)];
                })
              }
              onUpdate={(updates) =>
                setTopicMeasures((prev) => prev.map((m) => (m.actionId === action.id ? { ...m, ...updates } : m)))
              }
            />
          );
        })}

        <TransitionOtherActionCard
          measures={otherMeasures}
          disabled={disabled}
          onAdd={() => setTopicMeasures((prev) => [...prev, newMeasure(topicId, "other", { otherLabel: "" })])}
          onRemove={(measureId) => setTopicMeasures((prev) => prev.filter((m) => m.id !== measureId))}
          onLabelChange={(measureId, label) =>
            setTopicMeasures((prev) => prev.map((m) => (m.id === measureId ? { ...m, otherLabel: label } : m)))
          }
          renderRow={(measure) => (
            <TransitionActionRow
              action={{ id: "other", name: measure.otherLabel || "Other" }}
              measure={measure}
              metricUnits={catalog.metricUnits}
              policyOptions={policyTopics}
              disabled={disabled}
              currentTab={currentTab}
              fieldIdentifierPrefix={`transition-${topicId}`}
              onToggle={() => {}}
              onUpdate={(updates) =>
                setTopicMeasures((prev) => prev.map((m) => (m.id === measure.id ? { ...m, ...updates } : m)))
              }
            />
          )}
        />

        <TransitionReusePicker
          currentTopicId={topicId}
          allMeasures={allMeasures}
          disabled={disabled}
          onReuse={(source) =>
            setTopicMeasures((prev) => [
              ...prev,
              newMeasure(topicId, source.actionId, { reusedFromMeasureId: source.id, otherLabel: source.otherLabel }),
            ])
          }
        />
      </div>
    </SectionCard>
  );
}
